import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { contractsAdminAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Phase 24.3 — Needs Review badge.
 * Renders a compact pill on any contract row / detail header whose
 * `needs_review === true`. KR sees a "Mark reviewed" affordance; others
 * see only the pill.
 *
 * Variants:
 *   pill    — small pill only (default, for table rows)
 *   panel   — banner variant for detail-page header
 */
export function NeedsReviewBadge({
  contract,
  variant = 'pill',
  onReviewed,
  className = '',
}) {
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  if (!contract?.needs_review) return null;
  const isKR = isKRRole(user);

  const submit = async () => {
    if ((notes || '').trim().length < 5) {
      toast.error('Review notes must be at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      await contractsAdminAPI.dismissNeedsReview(contract.request_id, notes.trim());
      toast.success('Marked as reviewed');
      setShowDialog(false);
      setNotes('');
      onReviewed?.(contract.request_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark reviewed');
    } finally { setBusy(false); }
  };

  if (variant === 'panel') {
    return (
      <>
        <div
          className={`flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 ${className}`}
          data-testid={`needs-review-panel-${contract.request_id}`}
        >
          <div className="flex items-center gap-2">
            <AlertCircle size={16} className="shrink-0" />
            <div>
              <div className="font-medium">Needs Review</div>
              <div className="text-xs text-amber-800/80">
                {contract.needs_review_reason || 'Created before the unified Contract architecture. Please review and correct if needed.'}
              </div>
            </div>
          </div>
          {isKR && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 hover:bg-amber-100"
              onClick={() => setShowDialog(true)}
              data-testid={`needs-review-dismiss-btn-${contract.request_id}`}
            >
              Mark reviewed
            </Button>
          )}
        </div>
        {isKR && (
          <AlertDialog open={showDialog} onOpenChange={(v) => { if (!busy) setShowDialog(v); }}>
            <AlertDialogContent data-testid="needs-review-dismiss-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Mark this contract as reviewed?</AlertDialogTitle>
                <AlertDialogDescription>
                  The Needs Review badge will be removed. This is a manual audit trail — nothing is
                  deleted. Please note what you reviewed / corrected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Textarea
                rows={3}
                placeholder="e.g. Verified carrier, level, and writing number against carrier statement."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="needs-review-notes-input"
              />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy || (notes || '').trim().length < 5}
                  onClick={(e) => { e.preventDefault(); submit(); }}
                  data-testid="needs-review-dismiss-confirm"
                >
                  {busy ? 'Saving…' : 'Mark Reviewed'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </>
    );
  }

  // pill variant
  return (
    <Badge
      className={`bg-amber-100 text-amber-900 border border-amber-300 gap-1 ${className}`}
      title={contract.needs_review_reason || 'Legacy contract — needs KR review'}
      data-testid={`needs-review-pill-${contract.request_id}`}
    >
      <AlertCircle size={10} /> Needs Review
    </Badge>
  );
}

export default NeedsReviewBadge;
